import { createClient, type RealtimeChannel, type SupabaseClient } from '@supabase/supabase-js';
import { supabaseSupport, type SupportApi } from './support';
import type { Broadcast, Gift, ActivityItem, Design, DesignRecord, KhutbahLine, KhutbahSettings, Masjid, SavedTemplate, Schedule, Screen, ScreenBundle, TimetableDay } from '@noorview/core';
import { deviceLabel, lineOf, masjidColours, wordsOf, normalizeDesign, normalizeMasjid } from '@noorview/core';
import { ConflictError, type Backend, type Change, type NewBroadcast, type DeviceApi, type MasjidRegistration, type Member, type PhoneKey, type Presence, type Role, type Session, type Workspace } from './backend';
import { slotKey } from '../app/displaySlot';

/**
 * Production backend. All access rules live in the database (Row Level Security and checked functions), so this
 * client can only ever do what the signed-in person is allowed to do. Screens use a separate anonymous account each.
 */

const mapBroadcast = (r: Row): Broadcast => ({
  id: String(r.id), masjidId: String(r.masjid_id), kind: r.kind as Broadcast['kind'], screenIds: (r.screen_ids as string[] | null) ?? null,
  placement: r.placement as Broadcast['placement'], startsAt: String(r.starts_at), endsAt: (r.ends_at as string | null) ?? null, createdAt: String(r.created_at),
  message: (r.message as Broadcast['message']) ?? undefined, appeal: (r.appeal as Broadcast['appeal']) ?? undefined,
  khutbah: (r.khutbah as Broadcast['khutbah']) ?? undefined,
});

/**
 * What the listening screen is hearing right now, before the sentence is finished: sent straight from screen to
 * screens (a realtime broadcast, nothing saved), so the Arabic appears as it's spoken. One channel per khutbah,
 * shared by everything on this page that uses it.
 */
type Heard = { seq: number; text: string };
const partialFeeds = new Map<string, { ch: RealtimeChannel; fns: Set<(p: Heard) => void>; refs: number }>();
function partialFeed(c: SupabaseClient, broadcastId: string) {
  let f = partialFeeds.get(broadcastId);
  if (!f) {
    const fns = new Set<(p: Heard) => void>();
    const ch = c.channel(`khutbah-live:${broadcastId}`, { config: { broadcast: { self: true, ack: false } } })
      .on('broadcast', { event: 'partial' }, ({ payload }) => {
        const p = payload as Heard;
        if (Number.isInteger(p?.seq) && p.seq > 0 && typeof p.text === 'string') for (const fn of fns) fn({ seq: p.seq, text: p.text.slice(0, 1500) });
      })
      .subscribe();
    f = { ch, fns, refs: 0 };
    partialFeeds.set(broadcastId, f);
  }
  f.refs += 1;
  const feed = f;
  let done = false;
  return {
    feed,
    release() { if (done) return; done = true; if (--feed.refs <= 0) { partialFeeds.delete(broadcastId); void c.removeChannel(feed.ch); } },
  };
}

/**
 * A live khutbah's lines: everything so far, then each new or updated line as it's written (the database's live
 * feed), with a check every 10 seconds in case the feed drops something. While a sentence is being spoken, its saved
 * line holds the words settled so far and the English so far; the words heard after those come from the listening
 * screen as they're spoken, and are added on the end until the sentence is done.
 */
function watchLines(c: SupabaseClient, broadcastId: string, cb: (lines: KhutbahLine[]) => void): () => void {
  let alive = true;
  const shown = new Map<number, string>();
  const rows = new Map<number, KhutbahLine>();
  const heard = new Map<number, string>();
  const view = (seq: number): KhutbahLine | null => {
    const row = rows.get(seq), text = heard.get(seq);
    if (row && row.done !== false) return row;
    const words = text ? text.split(/\s+/).filter(Boolean) : [];
    if (!row) return words.length ? lineOf({ seq, arabic: [{ t: words.join(' '), k: 'p' }], english: null, ref: null, done: false }) : null;
    const more = words.slice(wordsOf(row.arabic).length);
    return more.length ? { ...row, arabic: [...row.arabic, { t: more.join(' '), k: 'p' }] } : row;
  };
  const emit = (seqs: Iterable<number>) => {
    const fresh: KhutbahLine[] = [];
    for (const seq of seqs) {
      const l = view(seq);
      if (!l) continue;
      const k = JSON.stringify(l);
      if (shown.get(seq) !== k) { shown.set(seq, k); fresh.push(l); }
    }
    if (fresh.length && alive) cb(fresh);
  };
  const give = (list: Row[]) => {
    const seqs: number[] = [];
    for (const r of list) {
      const l = lineOf(r as { seq: unknown; arabic: unknown; english: unknown; ref: unknown; done?: unknown });
      rows.set(l.seq, l);
      if (l.done !== false) heard.delete(l.seq);
      seqs.push(l.seq);
    }
    emit(seqs);
  };
  const all = async () => { const { data } = await c.from('khutbah_lines').select('seq, arabic, english, ref, done').eq('broadcast_id', broadcastId).order('seq'); if (data) give(data as Row[]); };
  const ch = c.channel(`khutbah:${broadcastId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'khutbah_lines', filter: `broadcast_id=eq.${broadcastId}` }, (p) => { if (p.new && 'seq' in p.new) give([p.new as Row]); })
    .subscribe((status) => { if (status === 'SUBSCRIBED') void all(); });
  const live = partialFeed(c, broadcastId);
  const onHeard = (p: Heard) => {
    // A finished sentence keeps its saved words.
    const row = rows.get(p.seq);
    if (!alive || (row && row.done !== false) || !p.text.trim()) return;
    heard.set(p.seq, p.text);
    emit([p.seq]);
  };
  live.feed.fns.add(onHeard);
  void all();
  const t = setInterval(() => void all(), 10_000);
  return () => { alive = false; clearInterval(t); live.feed.fns.delete(onHeard); live.release(); void c.removeChannel(ch); };
}

/** The masjid Noor MS was asked to help (from the address the first time, then remembered for this tab). */
const HELP_KEY = 'nv.helping';
function helpTarget(): string | null {
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('help') : null;
    if (q && /^[0-9a-f-]{36}$/i.test(q)) sessionStorage.setItem(HELP_KEY, q);
    return sessionStorage.getItem(HELP_KEY);
  } catch { return null; }
}
export function endHelp() { try { sessionStorage.removeItem(HELP_KEY); } catch { /* nothing kept */ } }

export class NoMasjidError extends Error { constructor() { super('This account isn’t linked to a masjid yet.'); } }

type Row = Record<string, unknown>;
const s = (v: unknown) => (v == null ? null : String(v));
const fail = (e: { message: string } | null) => { if (e) throw new Error(friendly(e.message)); };
function friendly(m: string) {
  if (/JWT|expired|invalid claim/i.test(m)) return 'Your session has ended. Please sign in again.';
  if (/Failed to fetch|NetworkError|network/i.test(m)) return 'Can’t reach NoorView. Check your internet connection.';
  return m;
}

const LINK_KEY = 'noorview.setPassword';
function rememberLink(on: boolean) { try { if (on) sessionStorage.setItem(LINK_KEY, '1'); else sessionStorage.removeItem(LINK_KEY); } catch { /* ignore */ } }

/**
 * Reset and invite emails land on /login with the result in the address: "#access_token=…&type=recovery" (or
 * type=invite) when the link worked, "#error=…&error_description=…" when it had expired or was already used.
 */
function readAuthLink(): { setPassword: boolean; error: string } {
  let p: URLSearchParams;
  try { p = new URLSearchParams(`${location.search.slice(1)}&${location.hash.replace(/^#/, '')}`); } catch { return { setPassword: false, error: '' }; }
  const err = p.get('error_description') || p.get('error');
  if (err) {
    try { history.replaceState(null, '', location.pathname); } catch { /* ignore */ }
    rememberLink(false);
    const expired = /expired|invalid|otp/i.test(`${err} ${p.get('error_code') ?? ''}`);
    return { setPassword: false, error: expired ? 'That link has expired or was already used. Ask for a new reset link below.' : err.replace(/\+/g, ' ') };
  }
  const type = p.get('type');
  if (p.get('access_token') && (type === 'recovery' || type === 'invite')) { rememberLink(true); return { setPassword: true, error: '' }; }
  let kept = false;
  try { kept = sessionStorage.getItem(LINK_KEY) === '1'; } catch { /* ignore */ }
  return { setPassword: kept, error: '' };
}

const mapDesign = (r: Row): DesignRecord => ({
  id: String(r.id), masjidId: String(r.masjid_id), name: String(r.name), draft: normalizeDesign(r.draft), draftRevision: Number(r.draft_revision),
  draftUpdatedAt: String(r.draft_updated_at), publishedVersion: Number(r.published_version), published: r.published ? normalizeDesign(r.published) : null, publishedAt: s(r.published_at),
});
const mapMasjid = (r: Row): Masjid => normalizeMasjid({
  id: String(r.id), name: String(r.name), city: (r.city as string) ?? undefined, timezone: String(r.timezone), latitude: Number(r.latitude), longitude: Number(r.longitude),
  prayer: r.prayer as Masjid['prayer'], plan: r.plan as Masjid['plan'], logo: (r.logo as string | null) ?? null, colors: masjidColours(r.colors), exitPinHash: (r.exit_pin_hash as string | null) ?? null,
  khutbahAddon: r.khutbah_addon === true, khutbah: (r.khutbah as Masjid['khutbah']) ?? null,
});

export class SupabaseBackend implements Backend {
  readonly kind = 'supabase' as const;
  private sb: SupabaseClient;
  private cached: Session | null = null;
  private masjidId: string | null = null;
  private readyP: Promise<void>;

  private link = { setPassword: false, error: '' };

  constructor(private url: string, private anonKey: string, private opts: { webOrigin?: string; fetch?: typeof fetch } = {}) {
    // Read what an emailed link asked for before the Supabase client takes the sign-in out of the address and clears it.
    this.link = readAuthLink();
    // Implicit flow: reset and invite links work even when opened on a different device or browser from the one that asked.
    this.sb = createClient(url, anonKey, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: 'implicit', storageKey: 'noorview-auth' }, ...(opts.fetch ? { global: { fetch: opts.fetch } } : {}) });
    this.sb.auth.onAuthStateChange((event) => { if (event === 'PASSWORD_RECOVERY') { this.link = { setPassword: true, error: '' }; rememberLink(true); } });
    this.readyP = this.refreshSession();
    this.sb.auth.onAuthStateChange((event, s) => {
      if (event === 'SIGNED_OUT') { this.cached = null; this.masjidId = null; this.rewire(); return; }
      // Every NoorView tab in a browser shares one sign-in. Signing in as someone else in another tab (a masjid's
      // account while this tab is Noor MS with remote access, say) would quietly switch this tab to that account,
      // so it starts again as whoever is signed in now.
      const id = s?.user?.id;
      if (id && this.cached && id !== this.cached.userId) { endHelp(); location.reload(); }
    });
  }

  ready() { return this.readyP; }

  private supportApi: SupportApi | null = null;
  get support() { return (this.supportApi ??= supabaseSupport(this.sb, () => this.mid())); }
  /** Where emailed links open: this site, or the web app when running inside the iPhone app. */
  private redirect() { const o = this.opts.webOrigin ?? location.origin; return o ? { redirectTo: `${o}/login` } : {}; }

  private async refreshSession() {
    const { data } = await this.sb.auth.getSession();
    const u = data.session?.user;
    if (!u || u.is_anonymous) { this.cached = null; return; }
    const { data: m } = await this.sb.from('members').select('masjid_id, role, name, email').eq('user_id', u.id).maybeSingle();
    // Noor MS with remote access to a masjid (a Pro masjid gave it; opened from NMS with ?help=<masjid>): no team
    // membership, just the time-limited access the masjid gave. The database allows it only while that lasts.
    const target = !m ? helpTarget() : null;
    if (target) {
      const { data: a } = await this.sb.from('support_access').select('expires_at').eq('masjid_id', target).gt('expires_at', new Date().toISOString()).maybeSingle();
      if (a) {
        this.masjidId = target;
        this.rewire();
        this.cached = { userId: u.id, email: u.email ?? '', name: 'Noor MS', role: 'admin', helping: { masjidId: target, until: a.expires_at as string } };
        return;
      }
      endHelp();
    }
    this.masjidId = (m?.masjid_id as string) ?? null;
    this.rewire();
    // The mark comes from the database when NoorClass makes the account (app_metadata: the person can't change it).
    const noorclassOnly = !m && (u.app_metadata as { signup_app?: string } | undefined)?.signup_app === 'noorclass';
    this.cached = { userId: u.id, email: u.email ?? '', name: (m?.name as string) || (u.email ?? '').split('@')[0]!, role: ((m?.role as Role) ?? 'owner'), ...(noorclassOnly ? { noorclassOnly: true } : {}) };
  }

  session() { return this.cached; }

  async signIn(email: string, password: string) {
    const { error } = await this.sb.auth.signInWithPassword({ email: email.trim(), password });
    if (error) throw new Error(/invalid/i.test(error.message) ? 'That email and password don’t match.' : friendly(error.message));
    await this.refreshSession();
    return this.cached!;
  }
  async signOut() { await this.sb.auth.signOut(); this.cached = null; }
  async requestPasswordReset(email: string) {
    const { error } = await this.sb.auth.resetPasswordForEmail(email.trim(), this.redirect());
    if (error && !/rate/i.test(error.message)) throw new Error(friendly(error.message));
  }

  async completePasswordReset(password: string) {
    if (password.length < 10) throw new Error('Use at least 10 characters.');
    const { data } = await this.sb.auth.getSession();
    if (!data.session) throw new Error('This link has expired. Go back to sign in and ask for a new reset link.');
    const { error } = await this.sb.auth.updateUser({ password });
    if (error) throw new Error(/different from the old/i.test(error.message) ? 'Choose a password you haven’t used before.' : /expired|missing|invalid/i.test(error.message) ? 'This link has expired. Go back to sign in and ask for a new reset link.' : friendly(error.message));
    this.clearAuthLink();
    await this.refreshSession();
  }

  /** Calls the masjid-onboarding function (no sign-in needed) and turns its errors into plain words. */
  private async onboarding<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.sb.functions.invoke('masjid-onboarding', { body });
    if (error) {
      const detail = await (error as { context?: Response }).context?.json?.().catch(() => null);
      throw new Error(friendly(detail?.error ?? (/fetch|network|relay/i.test(error.message) ? 'Couldn’t reach NoorView. Check the internet connection and try again.' : error.message)));
    }
    return data as T;
  }
  async leaveMasjid(newOwnerId?: string | null) {
    const { data, error } = await this.sb.rpc('leave_masjid', { p_masjid: this.mid(), p_new_owner: newOwnerId ?? null });
    fail(error);
    const r = (data ?? {}) as { closed?: boolean; closure?: string };
    // Tidy up and tell Noor MS (pictures, the sign-in). The account is already closed, so this never fails the leave.
    if (r.closed && r.closure) { try { await this.onboarding({ action: 'closed', closure: r.closure }); } catch { /* Noor MS still sees it in its dashboard */ } }
    await this.signOut().catch(() => {});
    return { closed: !!r.closed };
  }
  async registerMasjid(input: MasjidRegistration) {
    await this.onboarding({ action: 'register', ...input, email: input.email.trim().toLowerCase() });
  }
  async redeemCode(code: string) {
    const r = await this.onboarding<{ tokenHash: string; email: string }>({ action: 'redeem', code: code.replace(/\D/g, '') });
    const { error } = await this.sb.auth.verifyOtp({ token_hash: r.tokenHash, type: 'magiclink' });
    if (error) throw new Error('That code worked, but signing in didn’t. Please try again.');
    await this.refreshSession();
    return this.cached!;
  }
  async resendCode(email: string) {
    const r = await this.onboarding<{ message: string }>({ action: 'resend', email: email.trim().toLowerCase() });
    return r.message;
  }
  async changePassword(password: string) {
    if (password.length < 10) throw new Error('Use at least 10 characters.');
    const { error } = await this.sb.auth.updateUser({ password });
    if (error) throw new Error(/different from the old/i.test(error.message) ? 'Choose a password you haven’t used before.' : /reauth|recent/i.test(error.message) ? 'For your security, sign out and sign in again, then change your password.' : friendly(error.message));
  }

  authLink() { return this.link; }
  clearAuthLink() { this.link = { setPassword: false, error: '' }; rememberLink(false); }

  async createMasjid(input: { name: string; city: string; timezone: string; latitude: number; longitude: number; displayName: string }) {
    const { error } = await this.sb.rpc('create_masjid', { p_name: input.name, p_city: input.city, p_timezone: input.timezone, p_latitude: input.latitude, p_longitude: input.longitude, p_display_name: input.displayName });
    fail(error);
    await this.refreshSession();
  }

  private mid() { if (!this.masjidId) throw new NoMasjidError(); return this.masjidId; }

  async load(): Promise<Workspace> {
    if (!this.masjidId) await this.refreshSession();
    const m = this.mid();
    const [masjid, members, designs, screens, schedules, timetable, activity, devices, templates, broadcasts, uptime] = await Promise.all([
      this.sb.from('masjids').select('*').eq('id', m).single(),
      this.sb.from('members').select('user_id, role, name, email, joined_at').eq('masjid_id', m),
      this.sb.from('designs').select('*').eq('masjid_id', m),
      this.sb.from('screens').select('*').eq('masjid_id', m).order('created_at'),
      this.sb.from('schedules').select('*').eq('masjid_id', m),
      this.sb.from('timetable').select('date, times').eq('masjid_id', m).order('date').limit(5000),
      this.sb.from('activity').select('*').eq('masjid_id', m).order('at', { ascending: false }).limit(200),
      this.sb.from('devices').select('id, screen_id, last_seen_at, showing_version, user_agent'),
      this.sb.from('templates').select('*').eq('masjid_id', m).order('created_at'),
      this.sb.from('broadcasts').select('*').eq('masjid_id', m).or(`ends_at.is.null,ends_at.gt.${new Date(Date.now() - 7 * 86_400_000).toISOString()}`).order('starts_at'),
      this.sb.from('screen_uptime').select('screen_id, day, seconds').eq('masjid_id', m).gte('day', new Date(Date.now() - 31 * 86_400_000).toISOString().slice(0, 10)),
    ]);
    for (const r of [masjid, members, designs, screens, schedules, timetable, activity, devices, templates, broadcasts, uptime]) fail(r.error);
    const devs = (devices.data ?? []) as Row[];
    const presence: Presence[] = devs.map((d) => ({ deviceId: String(d.id), screenId: s(d.screen_id), lastSeenAt: s(d.last_seen_at) ?? new Date(0).toISOString(), version: d.showing_version == null ? null : Number(d.showing_version), userAgent: (d.user_agent as string) ?? undefined }));
    return {
      masjid: mapMasjid(masjid.data as Row),
      members: ((members.data ?? []) as Row[]).map((r) => ({ id: String(r.user_id), name: String(r.name || r.email), email: String(r.email), role: r.role as Role, joinedAt: String(r.joined_at) })),
      designs: ((designs.data ?? []) as Row[]).map(mapDesign),
      screens: ((screens.data ?? []) as Row[]).map((r): Screen => {
        const d = devs.find((x) => x.id === r.device_id);
        return { id: String(r.id), masjidId: m, name: String(r.name), designId: String(r.design_id), deviceId: s(r.device_id), power: r.power as Screen['power'], locked: !!r.locked, salahBlank: r.salah_blank !== false,
          lastSeenAt: d ? s(d.last_seen_at) : null, showingVersion: d?.showing_version == null ? null : Number(d.showing_version), deviceName: d ? deviceLabel(d.user_agent as string | null) : null, createdAt: String(r.created_at) };
      }),
      schedules: ((schedules.data ?? []) as Row[]).map((r): Schedule => ({ id: String(r.id), masjidId: m, screenId: String(r.screen_id), designId: String(r.design_id), title: String(r.title), startsAt: String(r.starts_at), durationMinutes: Number(r.duration_minutes), repeatWeekly: !!r.repeat_weekly, createdAt: String(r.created_at) })),
      timetable: ((timetable.data ?? []) as Row[]).map((r) => ({ ...(r.times as object), date: String(r.date) }) as TimetableDay),
      activity: ((activity.data ?? []) as Row[]).map((r): ActivityItem => ({ id: String(r.id), masjidId: m, kind: r.kind as ActivityItem['kind'], title: String(r.title), detail: String(r.detail), at: String(r.at), read: !!r.read })),
      presence,
      templates: ((templates.data ?? []) as Row[]).map((r): SavedTemplate => ({ id: String(r.id), masjidId: m, name: String(r.name), design: normalizeDesign(r.design), createdAt: String(r.created_at) })),
      broadcasts: ((broadcasts.data ?? []) as Row[]).map(mapBroadcast),
      uptime: ((uptime.data ?? []) as Row[]).map((r) => ({ screenId: String(r.screen_id), day: String(r.day), seconds: Number(r.seconds) })),
    };
  }

  /**
   * Live changes for the signed-in masjid. The channel follows the masjid: it opens once someone signs in (or a new
   * masjid's code is used), moves if they switch masjid, and closes when they sign out. It used to open only once, when
   * the app started, so a phone that signed in after starting never heard about new screens until it was reopened.
   */
  subscribe(cb: (c: Change) => void) {
    this.watchers.add(cb);
    if (!this.poll) {
      // Heartbeats are frequent and only change "last seen", so the dashboard checks them every 10 seconds instead.
      this.poll = setInterval(() => { if (this.masjidId && !document.hidden) for (const w of this.watchers) w({ kind: 'presence' }); }, 10_000);
    }
    void this.readyP.then(() => this.rewire());
    return () => {
      this.watchers.delete(cb);
      if (this.watchers.size) return;
      if (this.poll) { clearInterval(this.poll); this.poll = null; }
      this.rewire();
    };
  }
  private watchers = new Set<(c: Change) => void>();
  private poll: ReturnType<typeof setInterval> | null = null;
  private channel: { masjid: string; ch: RealtimeChannel } | null = null;
  private rewire() {
    const m = this.watchers.size ? this.masjidId : null;
    if (this.channel?.masjid === m) return;
    if (this.channel) { void this.sb.removeChannel(this.channel.ch); this.channel = null; }
    if (!m) return;
    const ch = this.sb.channel(`masjid:${m}`);
    const tell = () => { for (const w of this.watchers) w({ kind: 'workspace' }); };
    for (const table of ['screens', 'designs', 'schedules', 'activity', 'members', 'templates', 'broadcasts']) {
      ch.on('postgres_changes', { event: '*', schema: 'public', table, filter: `masjid_id=eq.${m}` }, tell);
    }
    ch.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'masjids', filter: `id=eq.${m}` }, tell);
    ch.subscribe();
    this.channel = { masjid: m, ch };
  }

  async updateMasjid(patch: Partial<Omit<Masjid, 'id'>>) {
    const row: Row = {};
    for (const k of ['name', 'city', 'timezone', 'latitude', 'longitude', 'prayer', 'logo'] as const) if (patch[k] !== undefined) row[k] = patch[k];
    if (patch.colors !== undefined) row.colors = masjidColours(patch.colors);
    if (patch.exitPinHash !== undefined) row.exit_pin_hash = patch.exitPinHash;
    const { error } = await this.sb.from('masjids').update(row).eq('id', this.mid());
    fail(error);
  }

  async createScreen({ name, design }: { name: string; design: Design }) {
    const m = this.mid();
    const d = await this.createDesign(name, design);
    const { data, error } = await this.sb.from('screens').insert({ masjid_id: m, name: name.slice(0, 40), design_id: d.id }).select('*').single();
    if (error) { await this.sb.from('designs').delete().eq('id', d.id); fail(error); }
    const r = data as Row;
    return { id: String(r.id), masjidId: m, name: String(r.name), designId: d.id, deviceId: null, power: 'on', locked: false, lastSeenAt: null, showingVersion: null, createdAt: String(r.created_at) } satisfies Screen;
  }
  async updateScreen(id: string, patch: Partial<Pick<Screen, 'name' | 'power' | 'locked' | 'designId' | 'salahBlank'>>) { await this.updateScreens([id], patch); }
  async updateScreens(ids: string[], patch: Partial<Pick<Screen, 'name' | 'power' | 'locked' | 'designId' | 'salahBlank'>>) {
    const row: Row = {};
    if (patch.name !== undefined) row.name = patch.name.slice(0, 40);
    if (patch.power) row.power = patch.power;
    if (patch.locked !== undefined) row.locked = patch.locked;
    if (patch.salahBlank !== undefined) row.salah_blank = patch.salahBlank;
    if (patch.designId) row.design_id = patch.designId;
    const { error } = await this.sb.from('screens').update(row).in('id', ids);
    fail(error);
  }
  async deleteScreen(id: string) {
    const { data } = await this.sb.from('screens').select('design_id').eq('id', id).single();
    const { error } = await this.sb.from('screens').delete().eq('id', id);
    fail(error);
    const designId = data?.design_id as string | undefined;
    if (designId) {
      const [{ count: a }, { count: b }] = await Promise.all([
        this.sb.from('screens').select('id', { count: 'exact', head: true }).eq('design_id', designId),
        this.sb.from('schedules').select('id', { count: 'exact', head: true }).eq('design_id', designId),
      ]);
      if (!a && !b) await this.sb.from('designs').delete().eq('id', designId);
    }
  }
  async pairScreen(code: string, screenId: string) { fail((await this.sb.rpc('pair_screen', { p_code: code, p_screen: screenId })).error); }
  async unpairScreen(screenId: string) { fail((await this.sb.rpc('unpair_screen', { p_screen: screenId })).error); }

  async saveDraft(designId: string, draft: Design, baseRevision: number, force = false) {
    const { data, error } = await this.sb.rpc('save_draft', { p_design: designId, p_draft: normalizeDesign(draft), p_base: baseRevision, p_force: force });
    if (error?.message === 'conflict') {
      const { data: latest } = await this.sb.from('designs').select('*').eq('id', designId).single();
      throw new ConflictError(mapDesign(latest as Row));
    }
    fail(error);
    const r = (data as Row[])[0]!;
    return { revision: Number(r.revision), savedAt: String(r.saved_at) };
  }
  async publish(designId: string) {
    const { data, error } = await this.sb.rpc('publish_design', { p_design: designId });
    fail(error);
    const r = (data as Row[])[0]!;
    return { version: Number(r.version), publishedAt: String(r.published_at) };
  }
  async createDesign(name: string, design: Design) {
    const { data, error } = await this.sb.from('designs').insert({ masjid_id: this.mid(), name: name.slice(0, 40), draft: normalizeDesign(design) }).select('*').single();
    fail(error);
    return mapDesign(data as Row);
  }
  async renameDesign(id: string, name: string) { fail((await this.sb.from('designs').update({ name: name.slice(0, 40) }).eq('id', id)).error); }
  async deleteDesign(id: string) { fail((await this.sb.from('designs').delete().eq('id', id)).error); }

  async setTimetable(days: TimetableDay[], mode: 'merge' | 'replace' | 'days') {
    fail((await this.sb.rpc('set_timetable', { p_masjid: this.mid(), p_days: days, p_mode: mode })).error);
  }
  async upsertSchedule(input: Omit<Schedule, 'id' | 'masjidId' | 'createdAt'> & { id?: string }) {
    const row = { masjid_id: this.mid(), screen_id: input.screenId, design_id: input.designId, title: input.title.slice(0, 60), starts_at: input.startsAt, duration_minutes: input.durationMinutes, repeat_weekly: input.repeatWeekly };
    const q = input.id ? this.sb.from('schedules').update(row).eq('id', input.id) : this.sb.from('schedules').insert(row);
    const { data, error } = await q.select('*').single();
    fail(error);
    const r = data as Row;
    return { id: String(r.id), masjidId: String(r.masjid_id), screenId: String(r.screen_id), designId: String(r.design_id), title: String(r.title), startsAt: String(r.starts_at), durationMinutes: Number(r.duration_minutes), repeatWeekly: !!r.repeat_weekly, createdAt: String(r.created_at) };
  }
  async deleteSchedule(id: string) { fail((await this.sb.from('schedules').delete().eq('id', id)).error); }
  async markActivityRead(ids?: string[]) {
    let q = this.sb.from('activity').update({ read: true }).eq('masjid_id', this.mid()).eq('read', false);
    if (ids) q = q.in('id', ids);
    fail((await q).error);
  }
  async uploadMedia(file: Blob, name: string) {
    const ext = file.type === 'image/webp' ? 'webp' : file.type === 'image/gif' ? 'gif' : file.type === 'image/png' ? 'png' : 'jpg';
    const path = `${this.mid()}/${crypto.randomUUID()}.${ext}`;
    const { error } = await this.sb.storage.from('noorview-media').upload(path, file, { contentType: file.type, cacheControl: '31536000', upsert: false });
    if (error) throw new Error(`Couldn’t upload ${name}: ${friendly(error.message)}`);
    return this.sb.storage.from('noorview-media').getPublicUrl(path).data.publicUrl;
  }
  async saveTemplate(name: string, design: Design) {
    const { data, error } = await this.sb.from('templates').insert({ masjid_id: this.mid(), name: name.trim().slice(0, 40) || 'My template', design: normalizeDesign(design) }).select('*').single();
    fail(error);
    const r = data as Row;
    return { id: String(r.id), masjidId: this.mid(), name: String(r.name), design: normalizeDesign(r.design), createdAt: String(r.created_at) };
  }
  async startBroadcast(b: NewBroadcast) {
    const { data, error } = await this.sb.from('broadcasts').insert({ masjid_id: this.mid(), kind: b.kind, screen_ids: b.screenIds, placement: b.placement, starts_at: b.startsAt, ends_at: b.endsAt, message: b.message ?? null, appeal: b.appeal ?? null }).select('*').single();
    fail(error);
    return mapBroadcast(data as Row);
  }
  async updateBroadcast(id: string, patch: Parameters<Backend['updateBroadcast']>[1]) {
    const row: Row = {};
    if (patch.startsAt) row.starts_at = patch.startsAt;
    if ('endsAt' in patch) row.ends_at = patch.endsAt;
    if ('screenIds' in patch) row.screen_ids = patch.screenIds;
    if (patch.placement) row.placement = patch.placement;
    if (patch.message) row.message = patch.message;
    // An appeal's title and target change inside the database in one step, so a gift added at the same moment by
    // someone else is never lost (migration 0014).
    if (patch.target != null || patch.title != null) {
      fail((await this.sb.rpc('update_appeal', { p_broadcast: id, p_title: patch.title ?? null, p_target: patch.target ?? null })).error);
    }
    if (Object.keys(row).length) fail((await this.sb.from('broadcasts').update(row).eq('id', id)).error);
  }
  async endBroadcast(id: string) { fail((await this.sb.from('broadcasts').update({ ends_at: new Date().toISOString() }).eq('id', id)).error); }

  async saveKhutbah(settings: KhutbahSettings) { fail((await this.sb.rpc('khutbah_save', { p_masjid: this.mid(), p_settings: settings })).error); }
  async startKhutbah(minutes = 60) { fail((await this.sb.rpc('khutbah_start', { p_masjid: this.mid(), p_minutes: minutes })).error); }
  async stopKhutbah() { fail((await this.sb.rpc('khutbah_stop', { p_masjid: this.mid() })).error); }
  async khutbahLines(broadcastId: string) {
    const { data, error } = await this.sb.from('khutbah_lines').select('seq, arabic, english, ref').eq('broadcast_id', broadcastId).order('seq');
    fail(error);
    return ((data ?? []) as Row[]).map((r) => lineOf(r as { seq: unknown; arabic: unknown; english: unknown; ref: unknown }));
  }
  watchKhutbah(broadcastId: string, cb: (lines: KhutbahLine[]) => void) { return watchLines(this.sb, broadcastId, cb); }
  async addGift(id: string, g: Omit<Gift, 'id' | 'at'>) { fail((await this.sb.rpc('add_gift', { p_broadcast: id, p_amount: g.amount, p_name: g.name, p_gender: g.gender })).error); }
  async removeGift(id: string, giftId: string) { fail((await this.sb.rpc('remove_gift', { p_broadcast: id, p_gift: giftId })).error); }

  async renameTemplate(id: string, name: string) { const { error } = await this.sb.from('templates').update({ name: name.trim().slice(0, 40) }).eq('id', id); fail(error); }
  async deleteTemplate(id: string) { const { error } = await this.sb.from('templates').delete().eq('id', id); fail(error); }

  async inviteMember(email: string, role: Role) {
    const { error } = await this.sb.functions.invoke('invite-member', { body: { email: email.trim().toLowerCase(), role, ...this.redirect() } });
    if (error) throw new Error(friendly((await (error as { context?: Response }).context?.json?.().catch(() => null))?.error ?? error.message));
  }
  async updateMemberRole(id: string, role: Role) {
    if (role === 'owner') throw new Error('There can only be one owner.');
    const { data, error } = await this.sb.from('members').update({ role }).eq('masjid_id', this.mid()).eq('user_id', id).select('user_id');
    fail(error);
    if (!data?.length) throw new Error('Only owners and admins can change roles, and the owner’s can’t be changed.');
  }
  async removeMember(id: string) { fail((await this.sb.from('members').delete().eq('masjid_id', this.mid()).eq('user_id', id)).error); }

  async phoneKeys(): Promise<PhoneKey[]> {
    const { data, error } = await this.sb.from('phone_keys').select('id, user_id, phone_name, created_at, last_used_at').eq('masjid_id', this.mid()).order('created_at');
    fail(error);
    return ((data ?? []) as Row[]).map((r) => ({ id: String(r.id), userId: String(r.user_id), phoneName: String(r.phone_name), createdAt: String(r.created_at), lastUsedAt: s(r.last_used_at) }));
  }
  async createPhoneKey(phoneName: string, replace?: string | null) {
    const { data, error } = await this.sb.rpc('create_phone_key', { p_masjid: this.mid(), p_phone: phoneName, p_replace: replace ?? null });
    fail(error);
    const r = data as { id: string; key: string };
    return { id: String(r.id), key: String(r.key) };
  }
  async removePhoneKey(id: string) { fail((await this.sb.from('phone_keys').delete().eq('id', id)).error); }

  /* ---------- the screen app ---------- */

  private deviceClient: SupabaseClient | null = null;
  private dc() {
    // A separate sign-in store, so a display opened on an editor's laptop never mixes up the two accounts.
    this.deviceClient ??= createClient(this.url, this.anonKey, { auth: { persistSession: true, autoRefreshToken: true, storageKey: slotKey('noorview-device-auth') } });
    return this.deviceClient;
  }

  device: DeviceApi = {
    register: async () => {
      const c = this.dc();
      const once = async () => {
        const { data } = await c.auth.getSession();
        if (!data.session) { const r = await c.auth.signInAnonymously(); fail(r.error); }
        return c.rpc('device_register', { p_user_agent: navigator.userAgent.slice(0, 200) });
      };
      let { data: rows, error } = await once();
      // This screen's sign-in no longer exists (an unpaired screen left off for a long time is tidied away nightly):
      // forget it and start afresh with a new pairing code, instead of failing until the stored sign-in expires.
      if (error && (error.code === '23503' || /jwt|token|sub claim|user.*(not|does not) exist/i.test(error.message ?? ''))) {
        await c.auth.signOut({ scope: 'local' }).catch(() => undefined);
        ({ data: rows, error } = await once());
      }
      fail(error);
      const r = (rows as Row[])[0]!;
      return { deviceId: String(r.device_id), code: s(r.code), screenId: s(r.screen_id) };
    },
    bundle: async () => {
      const { data, error } = await this.dc().rpc('device_bundle');
      fail(error);
      return (data as ScreenBundle | null) ?? null;
    },
    heartbeat: async (_deviceId, version) => { fail((await this.dc().rpc('device_heartbeat', { p_version: version })).error); },
    watch: (deviceId, cb) => {
      const c = this.dc();
      const ch = c.channel(`device:${deviceId}`)
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'device_signals', filter: `device_id=eq.${deviceId}` }, () => cb())
        .subscribe((status) => { if (status === 'SUBSCRIBED') cb(); });
      return () => { void c.removeChannel(ch); };
    },
    watchKhutbah: (broadcastId, cb) => watchLines(this.dc(), broadcastId, cb),
    listen: {
      token: async () => {
        const { data, error } = await this.dc().functions.invoke('live-khutbah', { body: { action: 'token' } });
        if (error) throw new Error(await functionError(error));
        return data as { apiKey: string; model: string; endsAt: string };
      },
      translate: async (line) => {
        const { error } = await this.dc().functions.invoke('live-khutbah', { body: { action: 'translate', ...line } });
        if (error) throw new Error(await functionError(error));
      },
      partial: (broadcastId) => {
        const live = partialFeed(this.dc(), broadcastId);
        return {
          send: (seq, text) => { void live.feed.ch.send({ type: 'broadcast', event: 'partial', payload: { seq, text } }).catch(() => undefined); },
          stop: () => live.release(),
        };
      },
      autoStart: async (slot, ends) => { fail((await this.dc().rpc('khutbah_auto_start', { p_slot: slot.toISOString(), p_ends: ends.toISOString() })).error); },
    },
  };
}

/** The message an edge function sent back with its error, when there is one. */
async function functionError(e: unknown): Promise<string> {
  const ctx = (e as { context?: Response }).context;
  try { const o = await ctx?.json(); if (o?.error) return String(o.error); } catch { /* not JSON */ }
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export type { Member };
